import { useState, useEffect, useCallback } from 'react';

import { getAnalyticsSectionState, setAnalyticsSectionState } from '../util/storage';

// Increment this version when defaults change to reset user preferences
const DEFAULTS_VERSION = 4;

const DEFAULT_SECTIONS = {
  statusOverview: true,
  classifications: false,
  hroTrends: false,
  allReports: false,
};

/**
 * useCollapsibleSections - Manages collapsible section state with AsyncStorage persistence
 *
 * @param {Object} initialState - Optional initial state override
 * @returns {Object} - Section states and control functions
 */
const useCollapsibleSections = (initialState = DEFAULT_SECTIONS) => {
  const [sectionStates, setSectionStates] = useState(initialState);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load saved state from AsyncStorage on mount
  useEffect(() => {
    const loadSavedState = async () => {
      try {
        const parsed = await getAnalyticsSectionState();
        if (parsed) {
          // Check if saved version matches current defaults version
          // If not, use new defaults (user will get fresh default experience)
          if (parsed._version === DEFAULTS_VERSION) {
            const { _version, ...sections } = parsed;
            setSectionStates({ ...initialState, ...sections });
          }
          // else: version mismatch, keep initialState (new defaults)
        }
      } catch (error) {
        console.warn('Failed to load collapsible section state:', error);
      } finally {
        setIsLoaded(true);
      }
    };

    loadSavedState();
  }, []);

  // Save state to AsyncStorage whenever it changes
  useEffect(() => {
    if (!isLoaded) return;

    const saveState = async () => {
      try {
        await setAnalyticsSectionState({ ...sectionStates, _version: DEFAULTS_VERSION });
      } catch (error) {
        console.warn('Failed to save collapsible section state:', error);
      }
    };

    saveState();
  }, [sectionStates, isLoaded]);

  const toggleSection = useCallback((sectionKey) => {
    setSectionStates((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  }, []);

  const expandAll = useCallback(() => {
    setSectionStates((prev) => {
      const expanded = {};
      Object.keys(prev).forEach((key) => {
        expanded[key] = true;
      });
      return expanded;
    });
  }, []);

  const collapseAll = useCallback(() => {
    setSectionStates((prev) => {
      const collapsed = {};
      Object.keys(prev).forEach((key) => {
        collapsed[key] = false;
      });
      return collapsed;
    });
  }, []);

  const allExpanded = Object.values(sectionStates).every((v) => v === true);
  const allCollapsed = Object.values(sectionStates).every((v) => v === false);

  return {
    sectionStates,
    toggleSection,
    expandAll,
    collapseAll,
    allExpanded,
    allCollapsed,
    isLoaded,
  };
};

export default useCollapsibleSections;
